import {
  Checkbox,
  ELinkVariant,
  ETextStyleVariant,
  Link,
  RadioSelector,
  SelectorGroup,
  Text,
} from '@outdoorsyco/bonfire';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { AddressInput } from '@/components/common/AddressInput';
import { ESearchFilters } from '@/constants/searchFilters';
import { ILocationSearchAutocompleteListOption } from '@/hooks/useLocationSearchAutocompleteOptions';
import { setMapboxPlace } from '@/redux/modules/mapboxPlace';
import { EFilterMethod, setFormFilter } from '@/redux/modules/searchForm';
import {
  getDeliveryCenter,
  getDeliveryDetails,
  getMapboxIdFromPlace,
} from '@/redux/selectors/mapboxPlace';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getSearchFormDeliveryFilter } from '@/redux/selectors/search/searchForm';
import { retrieveMapboxFullAddress } from '@/services/retrieveMapboxFullAddress';
import { EDeliveryOption } from '@/services/types/core/delivery.types';

type TLegacyDeliveryFilterProps = {
  isInMoreFilter?: boolean;
};

const LegacyDeliveryFilter: React.FC<TLegacyDeliveryFilterProps> = ({ isInMoreFilter }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const {
    [ESearchFilters.DELIVERY_STATIONARY]: deliveryStationaryFromStore,
    [ESearchFilters.STATIONARY_ACKNOWLEDGED]: stationaryAcknowledgedFromStore,
  } = useSelector(getSearchFormDeliveryFilter);
  const {
    [ESearchFilters.DELIVERY]: deliveryFromQuery,
    [ESearchFilters.DELIVERY_STATIONARY]: deliveryStationaryFromQuery,
    [ESearchFilters.ADDRESS]: deliveryAddressFromQuery,
    [ESearchFilters.DELIVERY_CENTER]: deliveryCenterFromQuery,
    [ESearchFilters.DELIVERY_DETAILS]: deliveryDetailsFromQuery,
  } = useSelector(getQueryParams);
  const mapboxIdFromPlace = useSelector(getMapboxIdFromPlace);
  const deliveryCenterFromPlace = useSelector(getDeliveryCenter);
  const deliveryDetailsFromPlace = useSelector(getDeliveryDetails);

  const insuranceValue: string = useMemo(() => {
    if (deliveryFromQuery !== 'true' && !deliveryStationaryFromStore) {
      return '';
    }

    return deliveryStationaryFromStore || '';
  }, [deliveryFromQuery, deliveryStationaryFromStore]);

  const [deliveryAddress, setDeliveryAddress] = useState<string>(
    (deliveryAddressFromQuery as string) || '',
  );

  // Set an initial value
  useEffect(() => {
    if (isInMoreFilter) return;
    const address = (deliveryAddressFromQuery as string) || '';
    setDeliveryAddress(address);
    dispatch(
      setFormFilter(
        {
          filters: {
            ...(deliveryFromQuery && {
              [ESearchFilters.DELIVERY]: deliveryFromQuery === 'true',
            }),
            ...(deliveryFromQuery === 'true' &&
              deliveryAddressFromQuery && {
                [ESearchFilters.DELIVERY_ADDRESS]: deliveryAddressFromQuery as string,
              }),
            ...(deliveryStationaryFromQuery && {
              [ESearchFilters.DELIVERY_STATIONARY]: deliveryStationaryFromQuery as string,
              [ESearchFilters.STATIONARY_ACKNOWLEDGED]:
                deliveryStationaryFromQuery === EDeliveryOption.STATIONARY,
            }),
            ...(deliveryCenterFromQuery && {
              [ESearchFilters.DELIVERY_CENTER]: deliveryCenterFromQuery as string,
            }),
            ...(deliveryDetailsFromQuery && {
              [ESearchFilters.DELIVERY_DETAILS]: deliveryDetailsFromQuery as string,
            }),
          },
          filterMethod: null,
        },
        true,
      ),
    );

    // Run only on mount to sync state with query parameters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectAddress = useCallback(
    async (option: ILocationSearchAutocompleteListOption) => {
      if (!option) {
        return;
      }

      const { label, value } = option;
      const selectedAddress = value?.name || value?.address || label;

      setDeliveryAddress(selectedAddress);

      const mapboxId = value?.mapbox_id || value?.id;
      let fullMapboxAddress = '';

      if (mapboxId) {
        fullMapboxAddress = await retrieveMapboxFullAddress(value);
      }

      if (value) {
        dispatch(setMapboxPlace(value));
      }

      dispatch(
        setFormFilter({
          filters: {
            [ESearchFilters.ADDRESS]: selectedAddress,
            [ESearchFilters.DELIVERY_ADDRESS]: selectedAddress,
            ...(mapboxId && { [ESearchFilters.MAPBOX_ID]: mapboxId }),
            ...(fullMapboxAddress && {
              [ESearchFilters.FULL_MAPBOX_ADDRESS]: fullMapboxAddress,
            }),
          },
          filterMethod: EFilterMethod.INPUT,
        }),
      );
    },
    [dispatch],
  );

  const handleSelectRadio = useCallback(
    (value: string) => {
      dispatch(
        setFormFilter({
          filters: {
            [ESearchFilters.DELIVERY]: true,
            [ESearchFilters.DELIVERY_ADDRESS]: deliveryAddress,
            [ESearchFilters.DELIVERY_STATIONARY]: value,
            [ESearchFilters.STATIONARY_ACKNOWLEDGED]: false,
            ...(mapboxIdFromPlace && { [ESearchFilters.MAPBOX_ID]: mapboxIdFromPlace }),
            ...(deliveryCenterFromPlace && {
              [ESearchFilters.DELIVERY_CENTER]: deliveryCenterFromPlace,
            }),
            ...(deliveryDetailsFromPlace && {
              [ESearchFilters.DELIVERY_DETAILS]: deliveryDetailsFromPlace,
            }),
          },
          filterMethod: EFilterMethod.RADIO,
        }),
      );
    },
    [
      dispatch,
      deliveryAddress,
      mapboxIdFromPlace,
      deliveryCenterFromPlace,
      deliveryDetailsFromPlace,
    ],
  );

  const handleStationaryAcknowledgedChange = useCallback(
    (value: boolean) => {
      dispatch(
        setFormFilter(
          {
            filters: {
              [ESearchFilters.STATIONARY_ACKNOWLEDGED]: value,
            },
            filterMethod: EFilterMethod.CHECKBOX,
          },
          true,
        ),
      );
    },
    [dispatch],
  );

  return (
    <div className="md:w-136">
      <div className="my-6">
        <AddressInput
          inputValue={deliveryAddress}
          onChange={setDeliveryAddress}
          onSelect={handleSelectAddress}
          label={intl.formatMessage({
            defaultMessage: 'Delivery location',
            id: 'Flx2cd',
          })}
        />
      </div>

      <Text variant={ETextStyleVariant.MediumRegular} className="mb-4">
        {intl.formatMessage({
          defaultMessage: 'Will you be driving the RV during your trip?',
          id: 'OwVnU9',
        })}
      </Text>

      <div className="mb-6">
        <SelectorGroup
          ariaLabel={intl.formatMessage({
            defaultMessage: 'Will you be driving the RV during your trip?',
            id: 'OwVnU9',
          })}
          value={insuranceValue}
          onChange={(value: string) => handleSelectRadio(value)}>
          <div className="flex flex-col gap-4 md:flex-row">
            <RadioSelector
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Yes, I plan to drive',
                id: 'mxA6k1',
              })}
              name="driving-option"
              value={EDeliveryOption.MOVING}
              label={intl.formatMessage({
                defaultMessage: 'Yes, I plan to drive',
                id: 'mxA6k1',
              })}
              description={intl.formatMessage({
                defaultMessage:
                  'Includes delivery to your location so you can hit the road after drop-off.',
                id: 'C0AjR6',
              })}
              withBorder
              showIndicator
              className="flex-1"
            />
            <RadioSelector
              ariaLabel={intl.formatMessage({
                defaultMessage: 'No, it will stay parked',
                id: 'n5+Rhk',
              })}
              name="driving-option"
              value={EDeliveryOption.STATIONARY}
              label={intl.formatMessage({
                defaultMessage: 'No, it will stay parked',
                id: 'n5+Rhk',
              })}
              description={intl.formatMessage({
                defaultMessage:
                  "The host will set everything up at your campsite or location. Since you aren't driving, we'll discount your insurance.",
                id: '0Wy0pq',
              })}
              withBorder
              showIndicator
              className="flex-1"
            />
          </div>
        </SelectorGroup>
      </div>

      {insuranceValue === EDeliveryOption.STATIONARY && (
        <>
          <Checkbox
            ariaLabel={intl.formatMessage({
              defaultMessage: 'I acknowledge that moving a stationary rental voids my insurance.',
              id: 'VJHMXk',
            })}
            content={{
              label: (
                <>
                  {intl.formatMessage(
                    {
                      defaultMessage:
                        'I acknowledge that moving a stationary rental voids my insurance. <link>Learn more about stationary delivery.</link>',
                      id: 'rTi8qJ',
                    },
                    {
                      link: (text: ReactNode) => (
                        <Link
                          target="_blank"
                          rel="noopener noreferrer"
                          href="/blog/stationary-rentals"
                          variant={ELinkVariant.Underline}>
                          {text}
                        </Link>
                      ),
                    },
                  )}

                  <span aria-hidden className="ml-1 text-red-800">
                    *
                  </span>
                </>
              ),
            }}
            value="acknowledged"
            isSelected={stationaryAcknowledgedFromStore}
            onChange={handleStationaryAcknowledgedChange}
            className="mb-6"
          />
        </>
      )}
    </div>
  );
};

export default LegacyDeliveryFilter;
